import { getUserFacingErrorMessage } from '@/lib/userFacingError';
export type SessionAutosaveStatus = 'idle' | 'dirty' | 'saving' | 'saved' | 'error' | 'retrying';

export interface SessionAutosaveControllerOptions<T> {
  persist: (value: T) => Promise<void>;
  isEqual?: (a: T, b: T) => boolean;
  onStatus?: (status: SessionAutosaveStatus, error: string | null) => void;
}

const defaultIsEqual = <T,>(a: T, b: T): boolean => Object.is(a, b);

/**
 * Framework-free autosave state machine used by `useSessionAutosave`.
 * Keeps latest-wins, stale-context ignore, and unchanged-skip behavior testable without jsdom.
 */
export class SessionAutosaveController<T> {
  private lastSaved: T;
  private contextKey = '';
  private generation = 0;
  private inFlight = false;
  private inFlightPromise: Promise<void> | null = null;
  private pendingAfterFlight = false;
  private latestValue: T;
  private persist: (value: T) => Promise<void>;
  private readonly isEqual: (a: T, b: T) => boolean;
  private readonly onStatus?: (status: SessionAutosaveStatus, error: string | null) => void;
  status: SessionAutosaveStatus = 'idle';
  error: string | null = null;

  constructor(initialValue: T, options: SessionAutosaveControllerOptions<T>) {
    this.latestValue = initialValue;
    this.lastSaved = initialValue;
    this.persist = options.persist;
    this.isEqual = options.isEqual ?? defaultIsEqual;
    this.onStatus = options.onStatus;
  }

  updatePersist(persist: (value: T) => Promise<void>): void {
    this.persist = persist;
  }

  setContextKey(contextKey: string, value: T): void {
    if (this.contextKey === contextKey) return;
    this.contextKey = contextKey;
    this.generation += 1;
    this.inFlight = false;
    this.pendingAfterFlight = false;
    this.latestValue = value;
    this.lastSaved = value;
    this.setStatus('idle', null);
  }

  setValue(value: T): void {
    this.latestValue = value;
  }

  private setStatus(status: SessionAutosaveStatus, error: string | null): void {
    this.status = status;
    this.error = error;
    this.onStatus?.(status, error);
  }

  async run(reason: 'autosave' | 'flush' | 'retry'): Promise<void> {
    const snapshot = this.latestValue;
    if (this.isEqual(snapshot, this.lastSaved)) {
      if (this.status !== 'error' && this.status !== 'retrying') {
        this.setStatus('idle', null);
      }
      return;
    }

    if (this.inFlight) {
      this.pendingAfterFlight = true;
      // An explicit flush/retry must reflect whether the attempt already running actually
      // settles, not just that another one was requested — otherwise retrying after a
      // timeout can resolve immediately while the original save is still outstanding, and
      // the next flush/retry would start a second, concurrent persist alongside it.
      return reason === 'autosave' ? undefined : (this.inFlightPromise ?? undefined);
    }

    this.inFlight = true;
    this.pendingAfterFlight = false;
    const generation = ++this.generation;
    const saveContext = this.contextKey;
    this.setStatus(reason === 'retry' ? 'retrying' : 'saving', null);

    const attempt = (async (): Promise<void> => {
      try {
        await this.persist(snapshot);
        if (generation !== this.generation || saveContext !== this.contextKey) {
          return;
        }
        this.lastSaved = snapshot;
        this.setStatus('saved', null);
      } catch (err) {
        if (generation !== this.generation || saveContext !== this.contextKey) {
          return;
        }
        this.setStatus('error', getUserFacingErrorMessage(err, 'Save failed'));
      } finally {
        this.inFlight = false;
        this.inFlightPromise = null;
        if (
          this.pendingAfterFlight
          && generation === this.generation
          && saveContext === this.contextKey
        ) {
          this.pendingAfterFlight = false;
          void this.run('autosave');
        }
      }
    })();
    this.inFlightPromise = attempt;
    return attempt;
  }
}
